import {
  CurrentUserOrganizationDto,
  PermissionDto,
  OrganizationDto,
} from '@sib/api';
import { create, StateCreator } from 'zustand';
import { createJSONStorage, persist, StateStorage } from 'zustand/middleware';
import {
  customExtensionStorage,
  shouldUseExtensionStorage,
} from './extension-storage';

const USER_SETUP_STORE_STORAGE_KEY = 'user-setup.store';

interface UserSetupOrganization {
  id: string;
  name: string;
}

interface UserSetupStore {
  organization: UserSetupOrganization;
  setOrganization: (organization: Partial<UserSetupOrganization>) => void;
  userRoles: string[];
  isSibEmployee: boolean | null;
  setIsSibEmployee: (isSibEmployee: boolean) => void;
  isSetupCompleted?: boolean;
  setIsSetupCompleted: (isSetupCompleted: boolean) => void;
  organizations: CurrentUserOrganizationDto[];
  setOrganizations: (organizations: CurrentUserOrganizationDto[]) => void;
  setUserRoles: (roles: string[]) => void;
  userPermissions: PermissionDto[];
  setUserPermissions: (permissions: PermissionDto[]) => void;
  parentOrganization: OrganizationDto | null;
  setParentOrganization: (org: OrganizationDto | null) => void;
  entryPointOrganization: OrganizationDto | null;
  setEntryPointOrganization: (org: OrganizationDto | null) => void;
  actionPermissions: Record<string, number> | null;
  setActionPermissions: (permissions: Record<string, number>) => void;
}

const stateCreator: StateCreator<UserSetupStore> = (set, getState) => ({
  organization: {
    id: '',
    name: '',
  },
  organizationId: '',
  userRoles: [],
  organizations: [],
  userPermissions: [],
  isSibEmployee: null,
  setIsSibEmployee: (isSibEmployee: boolean) => {
    set({ isSibEmployee });
  },
  isSetupCompleted: false,
  setIsSetupCompleted: (isSetupCompleted: boolean) => {
    set({ isSetupCompleted });
  },
  setOrganization: organization => {
    const currentOrganization = getState().organization;

    if (
      currentOrganization.id === organization.id &&
      currentOrganization.name === organization.name
    )
      return;

    set({
      organization: {
        ...currentOrganization,
        ...organization,
      },
    });
  },
  setUserRoles: (roles: string[]) => set({ userRoles: roles }),
  setOrganizations: (organizations: CurrentUserOrganizationDto[]) =>
    set({ organizations }),
  setUserPermissions: (permissions: PermissionDto[]) =>
    set({ userPermissions: permissions }),
  parentOrganization: null,
  setParentOrganization: (org: OrganizationDto | null) =>
    set({ parentOrganization: org }),
  entryPointOrganization: null,
  setEntryPointOrganization: (org: OrganizationDto | null) =>
    set({ entryPointOrganization: org }),
  actionPermissions: {},
  setActionPermissions: (permissions: Record<string, number>) =>
    set({ actionPermissions: permissions }),
});

export const useUserSetupStore = create<UserSetupStore>()(
  persist(stateCreator, {
    name: USER_SETUP_STORE_STORAGE_KEY,
    storage: createJSONStorage(() =>
      shouldUseExtensionStorage
        ? (customExtensionStorage as StateStorage)
        : localStorage
    ),
    partialize: ({
      organization,
      entryPointOrganization,
      parentOrganization,
      actionPermissions,
    }) => ({
      organization,
      entryPointOrganization,
      parentOrganization,
      actionPermissions,
    }),
  })
);
